import type { EmailTemplateListItem } from '@components/EmailTemplates/types';
import { INTERNAL_AUDIENCE, emailAudienceLabel } from '@helpers/emailAudience';

/**
 * The joined view of one email: its template, the rule that fires it (if any), and how it has
 * performed. `templateKey` is the spine — it is what `emailTemplates`, `email_automation_rules`,
 * `email_send_records` and `rule_fire_log` all join on — and nothing in the console presented it
 * as one thing until now.
 */

export type EmailFamily = 'transactional' | 'automation' | 'drip';

/**
 * Families are derived from the stored `category`, never guessed from the key.
 *
 * There are seven categories, not two: AUTOMATION_EMPLOYER (27), TRANSACTIONAL (16),
 * AUTOMATION_JOBSEEKER (14), DRIP_JOBSEEKER (8), DRIP_EMPLOYER (8), CONTENT (3), AUTH (2).
 *
 * CONTENT (new blog post and new guide announcements) is automation: each template is fired by
 * code that checks its ct-* rule first, so the rule is its trigger and its pause switch, exactly
 * as for the rule-engine automations.
 *
 * Drip is its own family rather than being folded into automation, and that is load-bearing: drip
 * steps are fired by the scheduler walking an enrollment, so they have no row in
 * `email_automation_rules`. Treated as automation, all 16 would fail the "has a rule?" test and
 * show as No trigger — sixteen red rows, all wrong, on a screen whose whole job is spotting the
 * unhealthy ones.
 */
const FAMILY_BY_CATEGORY: Record<string, EmailFamily> = {
  TRANSACTIONAL: 'transactional',
  AUTH: 'transactional',
  AUTOMATION_JOBSEEKER: 'automation',
  AUTOMATION_EMPLOYER: 'automation',
  CONTENT: 'automation',
  DRIP_JOBSEEKER: 'drip',
  DRIP_EMPLOYER: 'drip',
};

/** Unknown categories fall to transactional: "fired by code" is the honest default for a template
 *  nothing here can explain, and it is the one family that promises no configurability. */
export const familyOf = (category?: string | null): EmailFamily =>
  FAMILY_BY_CATEGORY[(category ?? '').toUpperCase()] ?? 'transactional';

export const FAMILY_LABELS: Record<EmailFamily, string> = {
  transactional: 'Transactional',
  automation: 'Automation',
  drip: 'Drip',
};

const AUDIENCE_BY_CATEGORY: Record<string, string> = {
  AUTOMATION_JOBSEEKER: 'Jobseekers',
  DRIP_JOBSEEKER: 'Jobseekers',
  AUTOMATION_EMPLOYER: 'Employers',
  DRIP_EMPLOYER: 'Employers',
};

export { INTERNAL_AUDIENCE };

/**
 * An internal email goes to a staff inbox, not to a user: the weekly referral summary
 * (`internal-referral-weekly`, rule int-1) and the ambassador sign-up sales alert
 * (`ambassador-signup-internal`). Both are categorised TRANSACTIONAL, so the category alone would
 * call them "Jobseekers & Employers". The key's naming convention says so when there is no rule.
 * Keys are lowercase-hyphen (TEMPLATE_KEY_FORMAT); the retired SCREAMING_SNAKE spelling is gone.
 */
export const isInternalEmail = (templateKey?: string | null): boolean => {
  const key = templateKey ?? '';
  return /^internal-/.test(key) || /-internal$/.test(key);
};

/**
 * Who the email goes to, most specific source first: the owning rule's audience (int-1 is
 * INTERNAL, the content announcements are ALL or SUBSCRIBERS), then the internal key convention,
 * then the category. Rule audiences go through the shared table, so a new one is humanised rather
 * than misfiled.
 *
 * Transactional mail with neither goes to whoever tripped it, so it names both rather than
 * claiming one.
 */
export const audienceOf = (
  category?: string | null,
  templateKey?: string | null,
  ruleAudience?: string | null,
): string => {
  if (ruleAudience && ruleAudience.trim()) return emailAudienceLabel(ruleAudience);
  if (isInternalEmail(templateKey)) return INTERNAL_AUDIENCE;
  return AUDIENCE_BY_CATEGORY[(category ?? '').toUpperCase()] ?? 'Jobseekers & Employers';
};

export type EmailStatus = 'Active' | 'Disabled' | 'No trigger' | 'Never sent';

export interface AutomationRuleLike {
  id?: string;
  ruleId?: string;
  audience?: string;
  status?: string;
  triggerType?: string;
  scheduleType?: string;
  templateKey?: string;
  cooldownDays?: number;
  delayMinutes?: number | null;
  windowHour?: number | null;
  cronExpression?: string | null;
  priority?: string;
  nextRun?: string | null;
  lastFiredAt?: string | null;
  lastEnqueueResult?: string | null;
}

export interface EmailRegistryRow {
  templateKey: string;
  name: string;
  category: string;
  family: EmailFamily;
  audience: string;
  subject: string;
  isActive: boolean;
  updatedAt: string | null;
  rule: AutomationRuleLike | null;
  /** Drip step index, when this template is a step. */
  dripStep: number | null;
  sent: number;
  openRate: number;
  clickRate: number;
  status: EmailStatus;
}

/**
 * Status, in priority order.
 *
 * A drip step can never be "No trigger": the sequence is its trigger, and it does not live in the
 * rules collection. Checking family before the rule join is what keeps that true.
 */
export const statusOf = (args: {
  family: EmailFamily;
  isActive: boolean;
  rule: AutomationRuleLike | null;
  sent: number;
}): EmailStatus => {
  const { family, isActive, rule, sent } = args;
  /*
   * "No trigger" claims nothing can fire this. Sends disprove that, whatever the mechanism.
   *
   * Not every AUTOMATION_* template is rule-driven: JOBS_REMAINING_REMINDER is categorised
   * AUTOMATION_EMPLOYER but is called straight from EmailTriggerService, so it has no rule and
   * sends perfectly well. Flagging it red would be the same false alarm the drip family exists to
   * avoid, just wearing a different hat — and there is no field that says "code fires this", so
   * the only honest signal is whether it has actually sent.
   *
   * Residual limit worth knowing: `sent` is a 30-day window, so a genuinely rule-less template
   * that fires less often than monthly can still show No trigger. That errs toward asking a
   * question rather than hiding one, and the record's copy says code-firing is a possibility.
   */
  if (family === 'automation' && !rule && sent === 0) return 'No trigger';
  if (!isActive) return 'Disabled';
  /*
   * The engine fires a rule only when its status is exactly ACTIVE (AutomationRuleEngineService:
   * findByConditionKeyAndStatus(..., "ACTIVE")). This checked for PAUSED alone, so a rule with no
   * status, or any status added later, read as Active on this screen while never firing.
   */
  if (rule && (rule.status ?? '').toUpperCase() !== 'ACTIVE') return 'Disabled';
  if (sent === 0) return 'Never sent';
  return 'Active';
};

/** `jobseeker-drip-3` -> 3. Null when the key is not a drip step. */
export const dripStepOf = (templateKey: string): number | null => {
  const m = /^(?:jobseeker|employer)-drip-(\d+)$/.exec(templateKey);
  return m ? Number(m[1]) : null;
};

/**
 * A rule that is switched on but has never fired.
 *
 * `docs/AUTOMATED_EMAIL_WORKFLOWS.md` records that only six schedulers actually run and that the
 * event hooks for several rules are not wired in the service layer, so "ACTIVE" in Mongo does not
 * mean "sending". That doc names the affected rule ids — deliberately not copied here, because a
 * hardcoded list becomes a lie the day engineering wires one up. `lastFiredAt` is the same fact
 * measured instead of asserted, and it corrects itself.
 */
export const isEnabledButNeverFired = (rule: AutomationRuleLike | null): boolean =>
  !!rule && (rule.status ?? '').toUpperCase() === 'ACTIVE' && !rule.lastFiredAt;

/**
 * Rates from `/api/email-analytics` are 0–1 floats; `/api/drip-campaigns/analytics` returns the
 * same idea as 0–100. Nothing in either payload says which you are holding, so the two are named
 * apart here rather than left to a reader to infer at the call site.
 */
export const formatRateFraction = (v: number): string => `${Math.round((v ?? 0) * 100)}%`;
export const formatRatePercent = (v: number): string => `${Math.round(v ?? 0)}%`;

interface TemplateStat {
  templateKey: string;
  sent: number;
  openRate: number;
  clickRate: number;
}

/**
 * Join templates + rules + analytics into the registry.
 *
 * There is no by-templateKey lookup for rules, so the caller fetches all of them (41 today, the
 * fetch asks for 200) and this indexes them once. Doing it per row would be a linear scan per
 * template.
 */
export const buildRegistry = (
  templates: EmailTemplateListItem[],
  rules: AutomationRuleLike[],
  stats: TemplateStat[],
): EmailRegistryRow[] => {
  const ruleByKey = new Map<string, AutomationRuleLike>();
  rules.forEach((r) => {
    if (r.templateKey) ruleByKey.set(r.templateKey, r);
  });
  const statByKey = new Map<string, TemplateStat>();
  stats.forEach((s) => statByKey.set(s.templateKey, s));

  return templates.map((t) => {
    const family = familyOf(t.category);
    // Any family but drip takes a rule when one names it, joined on templateKey alone with no list
    // of known rules. Transactional templates have them: int-1 (`internal-referral-weekly`) and the
    // ct-* content announcements are each fired by code that checks its rule first, so the rule
    // is the email's pause switch.
    // A drip step whose key happened to collide with a rule's templateKey must still not inherit
    // that rule's schedule as its own: the sequence is its trigger.
    const rule = family === 'drip' ? null : ruleByKey.get(t.templateKey) ?? null;
    const stat = statByKey.get(t.templateKey);
    const sent = stat?.sent ?? 0;

    return {
      templateKey: t.templateKey,
      name: t.name,
      category: t.category,
      family,
      audience: audienceOf(t.category, t.templateKey, rule?.audience),
      subject: t.subject,
      isActive: t.isActive,
      updatedAt: t.updatedAt,
      rule,
      dripStep: family === 'drip' ? dripStepOf(t.templateKey) : null,
      sent,
      openRate: stat?.openRate ?? 0,
      clickRate: stat?.clickRate ?? 0,
      status: statusOf({ family, isActive: t.isActive, rule, sent }),
    };
  });
};

/**
 * The row after its rule was paused or resumed: the rule as the server returned it (or, when it
 * answered without one, the status asked for), and the status recomputed from it. Shared by the
 * list's row switch and the record's button so the two cannot disagree on what "paused" shows.
 */
export const withRuleStatus = (
  row: EmailRegistryRow,
  paused: boolean,
  updated?: AutomationRuleLike | null,
): EmailRegistryRow => {
  if (!row.rule) return row;
  const rule = {
    ...row.rule,
    ...(updated ?? {}),
    status: updated?.status ?? (paused ? 'PAUSED' : 'ACTIVE'),
  };
  return {
    ...row,
    rule,
    status: statusOf({ family: row.family, isActive: row.isActive, rule, sent: row.sent }),
  };
};

export const isRuleActive = (rule: AutomationRuleLike | null): boolean =>
  (rule?.status ?? '').toUpperCase() === 'ACTIVE';

/** The one-line "when it fires" summary shared by the list and the record's identity rail. */
export const whenItFires = (row: EmailRegistryRow, dayOffset?: number | null): string => {
  if (row.family === 'transactional' && !row.rule) return 'Code-fired';
  if (row.family === 'drip') {
    return dayOffset != null
      ? `Sequence step ${row.dripStep ?? '?'}, day ${dayOffset}`
      : `Sequence step ${row.dripStep ?? '?'}`;
  }
  if (!row.rule) return '—';
  const trigger = (row.rule.triggerType ?? '').toLowerCase();
  const schedule = (row.rule.scheduleType ?? '').toLowerCase();
  const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
  return [cap(trigger), cap(schedule)].filter(Boolean).join(' · ') || '—';
};
